import { createFileRoute } from "@tanstack/react-router";
import { IsolatedFlag } from "@/components/system/IsolatedFlag";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trophy, Lock, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getMyBracket, submitPrediction, getBracketLeaderboard } from "@/lib/bracket.functions";
import { useLanguage } from "@/hooks/useLanguage";
import { GuerrillaInviteButton } from "@/components/viral/GuerrillaInviteButton";
import { bumpStreak } from "@/lib/social.functions";
import { trackEvent } from "@/lib/audit";
import { FomoTicker } from "@/components/social/FomoTicker";
import { CommunityMoments } from "@/components/social/CommunityMoments";
import { CinematicMomentsStream } from "@/components/cinematic/CinematicMomentsStream";
import { WorldStatusBanner } from "@/components/global/WorldStatusBanner";
import { GlobalEnergyBar } from "@/components/global/GlobalEnergyBar";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { NationGate } from "@/components/onboarding/NationGate";
import { ActivationBanner } from "@/components/onboarding/ActivationBanner";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { firePredictionWow } from "@/lib/wow/predictionWow";
import { MundialPredictionFloatingCard } from "@/components/predictions/MundialPredictionFloatingCard";
import { PREVIEW_MODE } from "@/lib/previewMode";
import { OFFICIAL_MATCH } from "@/lib/officialMatch";

export const Route = createFileRoute("/quinielas")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Quinielas Mundial 2026 — VerseSport" },
      {
        name: "description",
        content:
          "Juega tu quiniela del Mundial 2026 en VerseSport. Predice marcadores, suma puntos y compite con tu nación en tiempo real.",
      },
      { property: "og:title", content: "Quinielas Mundial 2026 — VerseSport" },
      {
        property: "og:description",
        content: "Predice marcadores, gana puntos y escala el ranking nacional.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://versesport.com/quinielas" },
      { property: "og:image", content: "https://versesport.com/og/mundial-2026.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Quinielas Mundial 2026 — VerseSport" },
      { name: "twitter:description", content: "Predice marcadores, gana puntos y escala el ranking nacional." },
      { name: "twitter:image", content: "https://versesport.com/og/mundial-2026.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://versesport.com/quinielas" }],
  }),
  component: QuinielasPage,
});

type MatchRow = { id: string; home_team: string; away_team: string; home_flag: string | null; away_flag: string | null; kickoff_at: string; status: string; real_home_score: number | null; real_away_score: number | null; stage: string | null };
type PredRow = { match_id: string; pred_home: number; pred_away: number; points_awarded: number; is_exact: boolean; locked_at: string | null };

function QuinielasPage() {
  const { t } = useLanguage();
  const qc = useQueryClient();
  const { nation } = useSelectedNation();
  const bracket = useQuery({ queryKey: ["bracket", "me"], queryFn: () => getMyBracket() });
  const leaderboard = useQuery({ queryKey: ["bracket", "lb"], queryFn: () => getBracketLeaderboard({ data: { limit: 50 } }), refetchInterval: 15000 });

  const mutate = useMutation({
    mutationFn: (vars: { match_id: string; pred_home: number; pred_away: number }) => submitPrediction({ data: vars }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["bracket", "me"] });
      void bumpStreak({ data: { streak_type: "prediction", delta: 1 } }).catch(() => {});
      qc.invalidateQueries({ queryKey: ["social", "streaks"] });
      // P0-FUNNEL-02B: advance Activation Funnel (prediction → share)
      // via the existing bootstrapActivationListener pipeline.
      void trackEvent("bracket_prediction_submitted", { match_id: vars.match_id });
      // P0-WOW-02: nation-emphasized WOW moment (idempotent per session).
      firePredictionWow({ nationCode: nation, matchId: vars.match_id });
    },
  });

  const matches = (bracket.data?.matches ?? []) as MatchRow[];
  const preds = new Map(((bracket.data?.predictions ?? []) as PredRow[]).map((p) => [p.match_id, p] as const));

  // PREVIEW MODE — Ensure the official World Cup Final (España vs
  // Argentina) always leads the quiniela list. Injected client-side as a
  // synthetic scheduled row; user predictions on it are ephemeral until
  // real fixtures land, but the card matches OFFICIAL_MATCH identity.
  const finalRow: MatchRow = {
    id: "official-final-esp-arg",
    home_team: OFFICIAL_MATCH.homeName,
    away_team: OFFICIAL_MATCH.awayName,
    home_flag: OFFICIAL_MATCH.homeFlag,
    away_flag: OFFICIAL_MATCH.awayFlag,
    kickoff_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    status: "scheduled",
    real_home_score: null,
    real_away_score: null,
    stage: OFFICIAL_MATCH.competition,
  };
  const orderedMatches = PREVIEW_MODE
    ? [finalRow, ...matches.filter((m) => m.id !== finalRow.id)]
    : matches;

  return (
    <main className="min-h-screen bg-background">
      <RouteTopBar fallback="/" />
      <NationGate surface="quinielas" mode="invite" />
      <div className="mx-auto max-w-4xl space-y-6 px-4 py-6">
      <ActivationBanner surface="quinielas" />
      <header className="flex items-center gap-2">
        <Trophy className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-black">{t("quinielas_title")}</h1>
        <div className="ml-auto"><FomoTicker compact /></div>
      </header>

      <section className="space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{t("quinielas_upcoming")}</h2>
        {bracket.isLoading && <p className="text-sm text-muted-foreground">{t("quinielas_loading")}</p>}
        {!bracket.isLoading && orderedMatches.length === 0 && (
          <p className="rounded-xl border border-border bg-background/60 p-4 text-sm text-muted-foreground">
            {t("quinielas_no_matches")}
          </p>
        )}
        <ul className="space-y-2">
          {orderedMatches.map((m) => (
            <MatchCard key={m.id} match={m} prediction={preds.get(m.id)} onSubmit={(h, a) => mutate.mutate({ match_id: m.id, pred_home: h, pred_away: a })} />
          ))}
        </ul>
      </section>

      {/* P0-UX-07 — wrap inline CTA in a positioned stacking context so it
          sits above MundialPredictionFloatingCard (fixed z-[55]) when they
          overlap in the bottom viewport region. Keeps the floating card
          visible while restoring click access to the invite button. */}
      <div className="relative z-[60]">
        <GuerrillaInviteButton />
      </div>

      {/* PR-40C-A — embed real Mundial Prediction Engine surface inside
          /quinielas. The hook falls back to globally-open prediction_events
          when no per-match match exists, so passing a stable sentinel ID
          surfaces the live engine output without touching the engine. */}
      <MundialPredictionFloatingCard matchId="quinielas-hub" />

      {/* Secondary surfaces — moved below first prediction for retention (P0). */}
      <IsolatedFlag name="WorldStatusBanner" flag="overlays"><WorldStatusBanner /></IsolatedFlag>
      <IsolatedFlag name="GlobalEnergyBar" flag="overlays"><GlobalEnergyBar compact /></IsolatedFlag>
      <CommunityMoments />
      <IsolatedFlag name="CinematicMomentsStream" flag="cinematic"><CinematicMomentsStream /></IsolatedFlag>

      <section className="space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">{t("quinielas_leaderboard")}</h2>
        <ol className="space-y-1 rounded-xl border border-border bg-background/60 p-2 text-sm">
          {(leaderboard.data?.rows ?? []).map((r, i) => {
            const uid = r.user_id ?? "anon";
            return (
              <li key={`${uid}-${i}`} className="flex items-center justify-between rounded-md bg-accent/30 px-2 py-1.5">
                <span className="font-bold">#{i + 1} · {uid.slice(0, 8)}</span>
                <span className="font-mono tabular-nums">{r.total_points ?? 0} {t("quinielas_pts")} · {r.exact_count ?? 0} {t("quinielas_exact")}</span>
              </li>
            );
          })}
          {(leaderboard.data?.rows ?? []).length === 0 && <li className="px-2 py-1.5 text-muted-foreground">{t("quinielas_no_scores")}</li>}
        </ol>
      </section>
      </div>
    </main>
  );
}

function MatchCard({ match, prediction, onSubmit }: { match: MatchRow; prediction?: PredRow; onSubmit: (h: number, a: number) => void }) {
  const { t } = useLanguage();
  const locked = new Date(match.kickoff_at).getTime() <= Date.now() || match.status !== "scheduled";
  const [h, setH] = useState<number>(prediction?.pred_home ?? 0);
  const [a, setA] = useState<number>(prediction?.pred_away ?? 0);
  useEffect(() => { if (prediction) { setH(prediction.pred_home); setA(prediction.pred_away); } }, [prediction]);

  return (
    <li className="rounded-xl border border-border bg-background/60 p-3">
      <div className="mb-2 flex items-center justify-between text-[11px] text-muted-foreground">
        <span>{new Date(match.kickoff_at).toLocaleString()}</span>
        <span className="flex items-center gap-1 font-bold uppercase">
          {locked ? <Lock className="h-3 w-3" /> : null}
          {match.status === "final" ? t("quinielas_status_final") : locked ? t("quinielas_status_locked") : t("quinielas_status_open")}
        </span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="flex-1 truncate font-bold">{match.home_flag ?? "🏳️"} {match.home_team}</span>
        <div className="flex items-center gap-1">
          <input type="number" min={0} max={20} value={h} disabled={locked} onChange={(e) => setH(Math.max(0, Math.min(20, Number(e.target.value) || 0)))}
            className="w-12 rounded-md border border-border bg-background px-2 py-1 text-center font-mono font-black tabular-nums disabled:opacity-50" />
          <span className="text-muted-foreground">·</span>
          <input type="number" min={0} max={20} value={a} disabled={locked} onChange={(e) => setA(Math.max(0, Math.min(20, Number(e.target.value) || 0)))}
            className="w-12 rounded-md border border-border bg-background px-2 py-1 text-center font-mono font-black tabular-nums disabled:opacity-50" />
        </div>
        <span className="flex-1 truncate text-right font-bold">{match.away_team} {match.away_flag ?? "🏳️"}</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
        <span className="text-muted-foreground">
          {match.status === "final" && match.real_home_score != null && (
            <>{t("quinielas_result")}: {match.real_home_score}–{match.real_away_score} · {prediction ? `+${prediction.points_awarded} ${t("quinielas_pts")}${prediction.is_exact ? " 🎯" : ""}` : t("quinielas_no_prediction")}</>
          )}
        </span>
        {!locked && (
          <button onClick={() => onSubmit(h, a)} className="flex items-center gap-1 rounded-md bg-primary px-3 py-1 text-xs font-black text-primary-foreground hover:opacity-90">
            <Check className="h-3 w-3" /> {t("quinielas_save")}
          </button>
        )}
      </div>
    </li>
  );
}